import { useCallback, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { genUUIDV4 } from '@frontend/string';
import { useAlert } from '@frontend/design-system';
import { useUploadMediaMutation } from '../../../../../hooks/api-hooks/use-media-upload';
import { useDynamicFormStore, useNonReactiveDynamicFormStore } from '../store';
import { abortUpload, clearAbortController, registerAbortController } from './abort-controllers';
import { getFieldAttachments, getFieldMediaIds, getFieldUploadStatus } from './selectors';
import { MediaAttachment, MediaErrorCode, MediaValidationRules } from './types';
import { getRemainingSlots, validateMediaFile } from './validation';

interface UseMediaFieldUploadArgs {
  /** The dynamic-form field key that owns these attachments. */
  fieldKey: string;
  /** Per-input validation rules (type/size/count). Controlled at the field level. */
  rules?: MediaValidationRules;
  /** Called with the committed server media ids whenever the field's successful uploads change. */
  onChange?: (mediaIds: string[]) => void;
}

export interface UseMediaFieldUploadResult {
  attachments: MediaAttachment[];
  isUploading: boolean;
  hasErrors: boolean;
  addFiles: (files: File[] | FileList) => void;
  retry: (attachmentId: string) => void;
  remove: (attachmentId: string) => void;
}

/**
 * UI-agnostic controller for immediate (per-attachment) media upload.
 *
 * Validates each file at add-time, uploads it immediately, and tracks loader/retry/error
 * state per attachment in the DynamicFormStore. Successful uploads are surfaced back to the
 * caller via `onChange(mediaIds)` so any UI/input can commit the value into form state.
 */
export const useMediaFieldUpload = ({
  fieldKey,
  rules = {},
  onChange,
}: UseMediaFieldUploadArgs): UseMediaFieldUploadResult => {
  const { attachments, addAttachment, updateAttachment, removeAttachment } = useDynamicFormStore([
    'attachments',
    'addAttachment',
    'updateAttachment',
    'removeAttachment',
  ]);
  const store = useNonReactiveDynamicFormStore();
  const uploadMutation = useUploadMediaMutation();
  const alerts = useAlert();
  const { t } = useTranslation('digital-forms-experience');

  const fieldAttachments = useMemo(() => getFieldAttachments(attachments, fieldKey), [attachments, fieldKey]);
  const status = useMemo(() => getFieldUploadStatus(attachments, fieldKey), [attachments, fieldKey]);

  const emitChange = useCallback(() => {
    onChange?.(getFieldMediaIds(store.getState().attachments, fieldKey));
  }, [onChange, store, fieldKey]);

  const runUpload = useCallback(
    async (attachment: MediaAttachment) => {
      const controller = registerAbortController(attachment.id);
      updateAttachment(attachment.id, { status: 'uploading', errorCode: undefined, message: undefined });

      try {
        const mediaId = await uploadMutation.mutateAsync({ file: attachment.file, signal: controller.signal });
        updateAttachment(attachment.id, { status: 'success', mediaId });
      } catch {
        if (controller.signal.aborted) {
          removeAttachment(attachment.id);
        } else {
          updateAttachment(attachment.id, {
            status: 'error',
            errorCode: MediaErrorCode.UploadFailed,
            message: t('Upload failed. Please try again.'),
          });
        }
      } finally {
        clearAbortController(attachment.id);
        emitChange();
      }
    },
    [uploadMutation, updateAttachment, removeAttachment, emitChange, t]
  );

  const addFiles = useCallback(
    (files: File[] | FileList) => {
      const incoming = Array.from(files);
      let remainingSlots = getRemainingSlots(getFieldAttachments(store.getState().attachments, fieldKey).length, rules);
      let limitAlertShown = false;

      incoming.forEach((file) => {
        // Over the per-field limit: reject the file outright and surface a single alert.
        if (remainingSlots <= 0) {
          if (!limitAlertShown) {
            alerts.error(
              rules.maxFiles
                ? t('You can upload at most {{count}} file(s)', { count: rules.maxFiles })
                : t('Too many files')
            );
            limitAlertShown = true;
          }
          return;
        }

        const fileCheck = validateMediaFile(file, rules, t);
        if (!fileCheck.valid) {
          alerts.error(fileCheck.message || t('Invalid file'));
          return;
        }

        const id = genUUIDV4();
        // TODO: Move preview URL creation into presentation components instead of attachment/upload hooks.
        const previewUrl = URL.createObjectURL(file);
        const attachment: MediaAttachment = { id, fieldKey, file, previewUrl, status: 'uploading' };

        addAttachment(attachment);
        void runUpload(attachment);

        remainingSlots -= 1;
      });
    },
    [store, fieldKey, rules, addAttachment, runUpload, alerts, t]
  );

  const retry = useCallback(
    (attachmentId: string) => {
      const attachment = store.getState().attachments[attachmentId];
      if (!attachment) return;

      void runUpload(attachment);
    },
    [store, runUpload]
  );

  const remove = useCallback(
    (attachmentId: string) => {
      const attachment = store.getState().attachments[attachmentId];
      if (!attachment) return;

      abortUpload(attachmentId);
      removeAttachment(attachmentId);

      emitChange();
    },
    [store, removeAttachment, emitChange]
  );

  return {
    attachments: fieldAttachments,
    isUploading: status.isUploading,
    hasErrors: status.hasError,
    addFiles,
    retry,
    remove,
  };
};
